// src/components/core/navigation.ts

import {
	createElement,
	getElementByIdOrThrow,
	getElementByIdSafe,
} from "../../utils/domHelpers";
import { getAuthToken, logout } from "../../services/authService";
import { showLoginOffCanvas, showRegisterOffCanvas } from "../modals/authModal";
import { getCookie } from "../../utils/cookies";
import { isAppDevEnvironment } from "../../utils/appEnv";

// List of pages where the leaderboard SHOULD BE HIDDEN.
const LEADERBOARD_EXCLUDE_PATHS = [
	"/contact-us", // Example: Hide on the contact page
	"/profile",
	"/leaderboard",
	"/dashboard/timeline",
	"/for-courses", // B2B page: keep focus on course content
	"/survey/ace-society-championship", // Example: Hide on a user profile page
	"/enter-v2/", // Hide on entry pages (matches all /enter-v2/* routes)
	"/enter/",
	"/web/",
	...(isAppDevEnvironment() ? ["/dev/"] : []),
];

// List of pages where the navigation bar SHOULD BE HIDDEN.
const NAV_EXCLUDE_PATHS = [
	"/enter/",
	"/enter-v2/",
	"/web/",
	...(isAppDevEnvironment() ? ["/dev/"] : []),
];

let isListenerAttached = false;

/**
 * Updates the visibility of the navigation bar based on the current path.
 */
export function updateNavVisibility(): void {
	const navbar = document.querySelector("nav");
	if (!navbar) return;

	const currentPath = window.location.pathname;
	const shouldHideNav = NAV_EXCLUDE_PATHS.some((excludePath) => {
		if (excludePath === "/") {
			return currentPath === "/";
		}
		return currentPath.startsWith(excludePath);
	});

	if (shouldHideNav) {
		navbar.classList.add("d-none");
	} else {
		navbar.classList.remove("d-none");
	}
}

/**
 * Hides leaderboard rows on scroll and shows them at the top.
 */
function setupLeaderboardScrollHandler(): void {
	if (isListenerAttached) {
		return; // Ensure the listener is only added once
	}

	const handleScroll = () => {
		// BUG FIX: If the user has manually closed the leaderboard, stop the scroll handler from interfering.
		if (sessionStorage.getItem("leaderboardClosed") === "true") {
			return;
		}

		const mobileLeaderboard = document.getElementById("mobile-leaderboard-row");
		const desktopLeaderboard = document.getElementById("desktop-leaderboard-row");

		// Check if the user has scrolled down
		const isScrolled = window.scrollY > 0;

		if (mobileLeaderboard) {
			mobileLeaderboard.classList.toggle("leaderboard-hidden", isScrolled);
		}
		if (desktopLeaderboard) {
			desktopLeaderboard.classList.toggle("leaderboard-hidden", isScrolled);
		}
	};

	window.addEventListener("scroll", handleScroll);
	isListenerAttached = true;
}

/**
 * Interface for user leaderboard data
 */
interface UserLeaderboardData {
	rank: number;
	totalPoints: number;
	timelineUrl?: string;
}

/**
 * Creates a single, responsive leaderboard section using Bootstrap's grid.
 * It now handles both logged-in and logged-out states.
 */
function createLeaderboardSection(
	container: HTMLElement,
	isLoggedIn: boolean,
	userData?: UserLeaderboardData | null,
): void {
	const leaderboardContainer = createElement(
		"div",
		{
			className: "leaderboard-unified w-100 leaderboard-desktop-faded mt-1 p-2",
			id: `leaderboard-info-${container.id}`,
		},
		container,
	);

	/* Close button is commented out as per your last update
    const closeButton = createElement( ... );
    closeButton.addEventListener('click', () => { ... });
    */

	const row = createElement(
		"div",
		{ className: "row align-items-center justify-content-center text-center" },
		leaderboardContainer,
	);

	// The title column is now a flex container to align the badge and text
	const titleCol = createElement(
		"div",
		// FIX: Explicitly set order for large screens
		{
			className:
				"col-12 col-lg-auto mb-2 mb-lg-0 d-flex align-items-center justify-content-center order-lg-1",
		},
		row,
	);

	// Create the championship text
	createElement(
		"span",
		{
			textContent: `🏆 Ace Society Championship`,
			className: "text-white text-uppercase fw-bold",
		},
		titleCol,
	);

	// Only show Rank and Points if the user is logged in
	if (isLoggedIn && userData) {
		const rankCol = createElement(
			"div",
			// FIX: Explicitly set order for large screens
			{ className: "col-6 col-xs-2 col-lg-auto mb-2 mb-lg-0 p-0 order-lg-2" },
			row,
		);
		const rankContainer = createElement(
			"span",
			{ className: "d-inline-flex align-items-center" },
			rankCol,
		);
		createElement(
			"small",
			{ textContent: `Your Rank: `, className: "text-white ms-lg-2 small" },
			rankContainer,
		);
		createElement(
			"span",
			{ textContent: `#${userData.rank}`, className: "fw-bold text-warning ms-2" },
			rankContainer,
		);

		const pointsCol = createElement(
			"div",
			// FIX: Explicitly set order for large screens
			{ className: "col-6 col-sm-3 col-lg-auto mb-2 mb-lg-0 p-0 order-lg-3" },
			row,
		);
		const pointsContainer = createElement(
			"span",
			{ className: "d-inline-flex align-items-center" },
			pointsCol,
		);
		createElement(
			"small",
			{ textContent: `Your Points: `, className: "text-white ms-lg-2 small" },
			pointsContainer,
		);
		createElement(
			"span",
			{
				textContent: userData.totalPoints.toLocaleString(),
				className: "fw-bold text-warning ms-2",
			},
			pointsContainer,
		);
	}

	// Show a different button based on login state
	const buttonCol = createElement(
		"div",
		// FIX: Explicitly set order for large screens
		{ className: "col-12 col-sm-3 col-lg-auto order-lg-4" },
		row,
	);
	if (isLoggedIn && userData) {
		createElement(
			"a",
			{
				className: "btn btn-success btn-extra-sm px-3 ms-lg-2",
				innerHTML: "View Your Timeline",
				attributes: {
					href: userData.timelineUrl || "/dashboard/timeline",
					"aria-label": "View your timeline",
				},
			},
			buttonCol,
		);
	} else {
		createElement(
			"a",
			{
				className: "btn btn-success btn-extra-sm px-3 ms-lg-2",
				innerHTML: "View Leaderboard and Prizes",
				attributes: {
					href: "/leaderboard", // Link to a generic leaderboard page
					"aria-label": "View Leaderboard and Prizes",
				},
			},
			buttonCol,
		);
	}
}

/**
 * Creates and renders the entire navbar with all its content
 */
export function createAndRenderNav(): void {
	let navbar = document.querySelector("nav");

	if (!navbar) {
		navbar = document.createElement("nav");
		document.body.insertBefore(navbar, document.body.firstChild);
	}

	navbar.innerHTML = "";
	navbar.className = "navbar navbar-dark navbar-expand-xxl fixed-top p-2 shadow";
	navbar.setAttribute(
		"style",
		"background-color: rgba(0,0,0,0.7); color: #ffffff;",
	);

	const containerFluid = createElement(
		"div",
		{ className: "container-fluid" },
		navbar,
	);
	const brandLink = createElement(
		"a",
		{
			className: "navbar-brand",
			id: "navbar-brand",
			attributes: { href: "/" },
		},
		containerFluid,
	);

	createElement(
		"img",
		{
			attributes: {
				src: "/static/img/acesocietylogo.svg",
				alt: "Ace Society Logo",
				height: "auto",
				width: "auto",
				style: "filter: invert(1);",
			},
		},
		brandLink,
	);

	const rightSideContainer = createElement(
		"div",
		{ className: "d-flex ms-auto" },
		containerFluid,
	);
	containerFluid.style.justifyContent = "space-between";

	const isLoggedIn = !!getAuthToken();
	const isTemporary = getCookie("is_temporary") === "true";

	const rank = sessionStorage.getItem("rank");
	const totalScore = sessionStorage.getItem("total_score");

	let userData: UserLeaderboardData | null = null;
	if (isLoggedIn && rank && totalScore) {
		userData = {
			rank: parseInt(rank, 10),
			totalPoints: parseInt(totalScore, 10),
			timelineUrl: "/dashboard/timeline",
		};
	}

	const creditsSection = createElement(
		"span",
		{ id: "credits", className: "d-flex ms-2" },
		rightSideContainer,
	);
	const userSection = createElement(
		"span",
		{ id: "navbar-user", className: "me-2" },
		rightSideContainer,
	);

	const toggleButton = createElement(
		"button",
		{
			className: "navbar-toggler navbar-dark",
			attributes: {
				type: "button",
				"data-bs-toggle": "collapse",
				"data-bs-target": "#navbarSupportedContent",
				"aria-controls": "navbarSupportedContent",
				"aria-expanded": "false",
				"aria-label": "Toggle navigation",
			},
		},
		rightSideContainer,
	);

	createElement("span", { className: "navbar-toggler-icon" }, toggleButton);

	const navbarCollapse = createElement(
		"div",
		{
			className: "collapse navbar-collapse",
			id: "navbarSupportedContent",
		},
		containerFluid,
	);

	const navbarList = createElement(
		"ul",
		{
			className: "navbar-nav ms-auto mb-2 mb-lg-0",
			id: "navbar-ul",
		},
		navbarCollapse,
	);

	const navItems: {
		text: string;
		href: string;
		onclick?: (e?: MouseEvent) => void;
		requiresAuth?: boolean;
		requiresNonTemp?: boolean;
	}[] = [
		{ text: "Home", href: "/" },
		{ text: "Where To Play", href: "/#where-to-play", onclick: collapseNavbar },
		{
			text: "Your Timeline",
			href: "/dashboard/timeline",
			requiresAuth: true,
			requiresNonTemp: true,
		},
		{ text: "Leaderboard", href: "/leaderboard" },
		{ text: "Hall of Fame", href: "/hall-of-fame" },
		{ text: "For Courses", href: "/for-courses" },
		{
			text: "Dashboard",
			href: "/dashboard",
			requiresAuth: true,
			requiresNonTemp: true,
		},
		{
			text: "Entries",
			href: "/dashboard/entries",
			requiresAuth: true,
			requiresNonTemp: true,
		},
		{ text: "Contact", href: "/contact-us" },
		{
			text: "Login",
			href: "#",
			onclick: () => showLoginOffCanvas(),
			requiresAuth: false,
		},
		{ text: "Logout", href: "#", onclick: logout, requiresAuth: true },
	];

	function collapseNavbar() {
		const navbarCollapse = document.getElementById("navbarSupportedContent");
		if (navbarCollapse && navbarCollapse.classList.contains("show")) {
			navbarCollapse.classList.remove("show");
		}
	}

	navItems.forEach((item) => {
		const show =
			item.requiresAuth === undefined ||
			(item.requiresAuth === true &&
				isLoggedIn &&
				(item.requiresNonTemp ? !isTemporary : true)) ||
			(item.requiresAuth === false && !isLoggedIn);

		if (show) {
			const li = createElement("li", { className: "nav-item" });
			const a = createElement(
				"a",
				{
					className: "nav-link",
					textContent: item.text,
					attributes: { href: item.href, style: "color: #ffffff;" },
				},
				li,
			);

			a.addEventListener("click", (e) => {
				if (item.href === "#") {
					e.preventDefault();
				}
				if (item.onclick) {
					item.onclick(e);
				}
				collapseNavbar();
			});

			navbarList.appendChild(li);
		}
	});

	// LEADERBOARD LOGIC
	const currentPath = window.location.pathname;
	const isLeaderboardClosed =
		sessionStorage.getItem("leaderboardClosed") === "true";

	const shouldShowLeaderboard = !LEADERBOARD_EXCLUDE_PATHS.some(
		(excludePath) => {
			if (excludePath === "/") {
				return currentPath === "/";
			}
			return currentPath.startsWith(excludePath);
		},
	);

	if (shouldShowLeaderboard && !isLeaderboardClosed) {
		setTimeout(() => {
			const mobileLeaderboardRow = createElement("div", {
				className: "d-md-none fixed-top",
				id: "mobile-leaderboard-row",
				attributes: { style: "top: 60px; z-index: 1029;" },
			});

			const desktopLeaderboardRow = createElement("div", {
				className: "d-none d-md-flex fixed-top justify-content-center",
				id: "desktop-leaderboard-row",
				attributes: { style: "top: 88px; z-index: 1030; width: 100%;" },
			});

			if (navbar && navbar.parentNode) {
				navbar.parentNode.insertBefore(mobileLeaderboardRow, navbar.nextSibling);
				navbar.parentNode.insertBefore(desktopLeaderboardRow, navbar.nextSibling);

				createLeaderboardSection(mobileLeaderboardRow, isLoggedIn, userData);
				createLeaderboardSection(desktopLeaderboardRow, isLoggedIn, userData);

				setupLeaderboardScrollHandler();
			}
		}, 0);
	}

	// USER-SPECIFIC ELEMENTS
	if (isLoggedIn && !isTemporary) {
		createElement(
			"a",
			{
				className: "nav-link",
				attributes: { href: "/dashboard", title: "Dashboard" },
				innerHTML: '<i class="fa-solid fa-user fa-lg"></i>',
			},
			userSection,
		);

		const creditsBadge = createElement(
			"span",
			{
				id: "credits-badge",
				className: "badge text-bg-success",
				textContent: "0 credits",
			},
			creditsSection,
		);

		creditsBadge.style.cursor = "pointer";
		creditsBadge.onclick = () => {
			window.location.href = "/dashboard/purchase";
		};
		creditsBadge.setAttribute("role", "button");
		creditsBadge.setAttribute("aria-label", "Purchase more credits");
	} else if (!isLoggedIn) {
		const signupButton = createElement(
			"a",
			{
				className: "nav-link border border-2 border-success rounded p-2 text-white",
				id: "navSignUp",
				textContent: "Sign up",
				attributes: { href: "#" },
			},
			userSection,
		);
		signupButton.addEventListener("click", (e) => {
			e.preventDefault();
			showRegisterOffCanvas();
		});
	}

	setActiveNavLink();
	userSection.classList.add("nav-user-section");
	if (isLoggedIn && !isTemporary) {
		userSection.classList.add("nav-user-logged-in");
		creditsSection.classList.add("nav-credits-logged-in");
	} else {
		userSection.classList.add("nav-user-logged-out");
		creditsSection.classList.add("nav-credits-logged-out");
	}
	creditsSection.classList.add("nav-credits-section");
	const userLink = userSection.querySelector("a");
	if (userLink && !userLink.getAttribute("aria-label")) {
		userLink.setAttribute("aria-label", "User profile");
	}

	updateNavVisibility();
}

/**
 * Sets the text content of the credits badge in the navigation bar.
 */
export function setNavCredits(count: number): void {
	const creditsBadge = document.getElementById("credits-badge");
	if (creditsBadge) {
		creditsBadge.textContent = `${count} credit${count !== 1 ? "s" : ""}`;
	}
}

/**
 * Sets the active class on the navigation link corresponding to the current page path.
 */
function setActiveNavLink(): void {
	const path = window.location.pathname;
	const links = document.querySelectorAll<HTMLAnchorElement>(
		"#navbar-ul .nav-link",
	);
	links.forEach((link) => {
		link.classList.remove("active");
		const linkPath = link.getAttribute("href");
		if (
			linkPath === path ||
			(linkPath !== "/" && path.startsWith(linkPath + "/")) ||
			(linkPath === "/" && path === "/")
		) {
			if (linkPath === "/" && path !== "/") return;
			link.classList.add("active");
			link.setAttribute("aria-current", "page");
		} else {
			link.removeAttribute("aria-current");
		}
	});
}
